import { cx } from '@emotion/css';
import { Input } from '@sortlist-frontend/design-system/legacy';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { useDebounceCallback, useIsMounted } from '@sortlist-frontend/utils';
import { useCombobox } from 'downshift';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AgenciesAutocompletePayload, useAgenciesAutocomplete } from '_core/repos/agencies-autocomplete.repo';
import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';
import { AgencyAutocompleteSuggestions } from '_features/agency/claim/components';

import * as S from './styles';

const MAX_DROPDOWN_HEIGHT = 310;
const MIN_CHAR = 3;

type Props = {
  /** setting up an id to prevent issues with ssr (i.e: id did not match server) */
  id: string;
  onSelected: (fragment: string, item: AgenciesAutocompletePayload) => void;
};

export const AgencyAutocomplete = (props: Props) => {
  const { id, onSelected } = props;
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);
  const [fragment, setFragment] = useState('');
  const enabled = fragment.length >= MIN_CHAR;
  const { data, isLoading } = useAgenciesAutocomplete({ name: fragment }, enabled);
  const previousItems = useRef<AgenciesAutocompletePayload[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMounted = useIsMounted();
  const setDebouncedFragment = useCallback(
    useDebounceCallback((newFragment: string) => isMounted() && setFragment(newFragment), 300),
    [],
  );

  const items = isLoading ? previousItems.current : (data ?? []);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const {
    getInputProps,
    getComboboxProps,
    getItemProps,
    getMenuProps,
    selectedItem,
    highlightedIndex,
    isOpen,
    openMenu,
  } = useCombobox({
    id,
    items: items,
    itemToString: (item) => (item !== null ? item.name : ''),
    onInputValueChange: ({ inputValue }) => {
      // keep a reference to current data to prevent flickering while data is loading
      previousItems.current = data ?? [];
      setDebouncedFragment(inputValue ?? '');
    },
    onSelectedItemChange: (item) => {
      const { selectedItem } = item;
      if (selectedItem) {
        onSelected(fragment, selectedItem);
      }
    },
  });

  return (
    <S.Autocomplete {...getComboboxProps()} className="relative">
      <Input
        height="lg"
        className="shadow-2 rounded-md width-100"
        {...getInputProps({
          ref: inputRef,
          autoComplete: 'off',
          placeholder: t('agency:claim.landing.autocompletePlaceholder_V2'),
          onFocus: openMenu,
        })}
      />

      <S.Suggestions
        {...getMenuProps()}
        className={cx('absolute list-reset shadow-4 cursor-pointer bg-secondary-50 overflow-hidden rounded-sm', {
          'scale-down': isOpen,
        })}>
        {isOpen && (
          <AgencyAutocompleteSuggestions
            items={items}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            selectedItem={selectedItem}
          />
        )}
      </S.Suggestions>
    </S.Autocomplete>
  );
};
